import { useEffect, useState } from "react";
import { Download, Info, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, downloadPdf, formatApiError, inr } from "@/lib/api";
import { CATEGORIES, DURATIONS } from "@/lib/constants";
import { PageHeader, Panel, marginCls } from "@/components/admin/common";
import { PackageEditDialog } from "@/components/admin/PackageEditDialog";

export default function PackagesAdmin() {
  const [items, setItems] = useState([]);
  const [duration, setDuration] = useState("all");
  const [category, setCategory] = useState("all");
  const [editing, setEditing] = useState(null);

  useEffect(() => { api.get("/admin/packages").then((r) => setItems(r.data)); }, []);
  const replace = (p) => setItems((l) => l.map((x) => (x.id === p.id ? p : x)));
  const toggle = async (p, v) => {
    try { const { data } = await api.put(`/admin/packages/${p.id}`, { is_active: v }); replace(data); toast.success(v ? "Package visible on website" : "Package hidden from website"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const list = items.filter((p) => (duration === "all" || p.duration === duration) && (category === "all" || p.category === category || p.alt_categories?.includes(category)));

  return (
    <div data-testid="admin-packages-page">
      <PageHeader title="Packages & Prices" subtitle="Edit per-person costs, selling prices, itineraries and visibility." />
      <div className="mb-4 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="sample-prices-notice">
        <Info className="h-5 w-5 shrink-0" />
        <p>All prices were pre-filled as <b>sample examples</b>. Please edit each package to match your actual hotel, ferry and transfer rates before sharing quotes.</p>
      </div>
      <Panel className="p-4 mb-4 flex flex-col sm:flex-row gap-3">
        <Select value={duration} onValueChange={setDuration}>
          <SelectTrigger className="sm:w-48" data-testid="admin-package-duration-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">All durations</SelectItem>{DURATIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="sm:w-48" data-testid="admin-package-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">All categories</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </Panel>
      <Panel className="overflow-x-auto">
        <Table className="min-w-[960px]">
          <TableHeader><TableRow>
            <TableHead>Package</TableHead><TableHead>Duration</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Cost / person</TableHead><TableHead className="text-right">Selling / person</TableHead><TableHead className="text-right">Profit</TableHead><TableHead className="text-right">Margin</TableHead><TableHead className="text-center">On website</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {list.map((p) => (
              <TableRow key={p.id} data-testid={`admin-package-row-${p.slug}`}>
                <TableCell className="font-medium text-navy max-w-[240px] truncate">{p.name}</TableCell>
                <TableCell className="font-mono text-sm">{p.duration}</TableCell>
                <TableCell className="text-sm">{p.category}</TableCell>
                <TableCell className="text-right font-mono" data-testid={`admin-package-cost-${p.slug}`}>{inr(p.cost_price)}</TableCell>
                <TableCell className="text-right font-mono text-ocean" data-testid={`admin-package-selling-${p.slug}`}>{inr(p.selling_price)}</TableCell>
                <TableCell className={`text-right font-mono ${marginCls(p.margin)}`}>{inr(p.profit)}</TableCell>
                <TableCell className={`text-right font-mono font-semibold ${marginCls(p.margin)}`} data-testid={`admin-package-margin-${p.slug}`}>{p.margin}%</TableCell>
                <TableCell className="text-center"><Switch checked={p.is_active} onCheckedChange={(v) => toggle(p, v)} data-testid={`admin-package-active-${p.slug}`} /></TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Itinerary PDF" onClick={() => downloadPdf(`/admin/packages/${p.id}/itinerary.pdf`, `${p.slug}-itinerary.pdf`)} data-testid={`admin-package-pdf-${p.slug}`}><Download className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing(p)} data-testid={`admin-package-edit-${p.slug}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>
      <PackageEditDialog pkg={editing} onClose={() => setEditing(null)} onSaved={(p) => { replace(p); setEditing(null); }} />
    </div>
  );
}
